export type PrintDocumentInIframeParams = {
  /** A complete HTML document. */
  html: string;
  /** Called once the print dialog closes, or if printing can't start. */
  onDone?: () => void;
};

/**
 * Prints `html` from a hidden iframe that contains only that document, then
 * removes the iframe. Calling `window.print()` on the whole app page hangs
 * Chrome's print preview. Returns a function that cancels and cleans up.
 */
export const printDocumentInIframe = ({
  html,
  onDone,
}: PrintDocumentInIframeParams): (() => void) => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  Object.assign(iframe.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    border: '0',
  });

  let isFinished = false;
  const cleanUp = () => {
    isFinished = true;
    iframe.remove();
  };
  const finish = () => {
    if (isFinished) {
      return;
    }
    cleanUp();
    onDone?.();
  };

  iframe.addEventListener('load', () => {
    const frameWindow = iframe.contentWindow;
    if (!frameWindow) {
      finish();
      return;
    }
    frameWindow.addEventListener('afterprint', finish);
    try {
      frameWindow.focus();
      frameWindow.print();
    } catch {
      finish();
    }
  });
  iframe.srcdoc = html;
  document.body.appendChild(iframe);

  return cleanUp;
};
